import type { StampMapEntry, StampPosition } from "../api.js";
import { labelStyle, inputStyle, selectStyle, btnSecondary } from "./ui.js";
import { PropSelect, type HubSpotProp } from "./PropSelect.js";

/** 画面に最低限出す宛先の列数（宛先1・宛先2） */
const MIN_RECIPIENT_COLUMNS = 2;

/** 入力途中の空欄は NaN で持つ。保存時に clientSendOverrideModel.pruneStampMap が「置かない」に直す */
const blank = (): StampPosition => ({ x: NaN, y: NaN });
const show = (n: number | undefined) => (n === undefined || Number.isNaN(n) ? "" : String(n));

/**
 * 署名欄（押印）の座標の対応表（テンプレートを使わない送信）。
 *
 * 書類種別などの項目を選び、値ごとに「担当者（1 人目の署名者）・宛先1・宛先2」の x, y を登録する。
 * 送信のとき、1 本目のファイルの最後のページに置く（サーバーの lib/stamp-widgets.ts）。
 * 対応表に無い値・空の値のレコードは、署名欄の無い契約書を出さないよう送信を止める。
 */
export default function StampPositionsForm({
  prop,
  map,
  properties,
  onChange,
}: {
  prop: string;
  map: StampMapEntry[];
  properties: HubSpotProp[];
  onChange: (next: { stamp_prop: string; stamp_map: StampMapEntry[] }) => void;
}) {
  /**
   * 選択式の項目なら選択肢から選ばせる。**自由入力にしてはいけない**
   * （レコードに入っているのは内部値で、ラベルを手入力すると永久に一致しない）
   */
  const selectedProp = properties.find((p) => p.name === prop);
  const propOptions = selectedProp?.options ?? [];
  const isEnum = propOptions.length > 0;

  const recipientColumns = Math.max(MIN_RECIPIENT_COLUMNS, ...map.map((e) => e.recipients?.length ?? 0));
  const roles = [
    { key: "first_signer" as const, label: "担当者" },
    ...Array.from({ length: recipientColumns }, (_, i) => ({ key: i, label: `宛先${i + 1}` })),
  ];

  const setMap = (next: StampMapEntry[]) => onChange({ stamp_prop: prop, stamp_map: next });

  const positionOf = (e: StampMapEntry, role: "first_signer" | number): StampPosition | null | undefined =>
    role === "first_signer" ? e.first_signer : e.recipients?.[role];

  const setPosition = (i: number, role: "first_signer" | number, axis: "x" | "y", raw: string) => {
    const e = map[i];
    const current = positionOf(e, role) ?? blank();
    const n = raw.trim() === "" ? NaN : Number(raw);
    const nextPos: StampPosition = { ...current, [axis]: n };
    // 両方空なら「置かない」
    const value = Number.isNaN(nextPos.x) && Number.isNaN(nextPos.y) ? null : nextPos;
    const nextEntry: StampMapEntry =
      role === "first_signer"
        ? { ...e, first_signer: value }
        : {
            ...e,
            recipients: Array.from({ length: Math.max(recipientColumns, e.recipients?.length ?? 0) }, (_, k) =>
              k === role ? value : e.recipients?.[k] ?? null,
            ),
          };
    const next = [...map];
    next[i] = nextEntry;
    setMap(next);
  };

  const numberInput = (i: number, role: "first_signer" | number, axis: "x" | "y", label: string) => {
    const pos = positionOf(map[i], role);
    return (
      <input
        type="number"
        min={0}
        step={1}
        inputMode="numeric"
        aria-label={label}
        placeholder={axis}
        style={{ ...inputStyle, width: 84, padding: "6px 8px" }}
        value={show(pos?.[axis])}
        onChange={(ev) => setPosition(i, role, axis, ev.target.value)}
      />
    );
  };

  const isHalf = (p: StampPosition | null | undefined) =>
    !!p && Number.isNaN(p.x) !== Number.isNaN(p.y);

  return (
    <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid #e8e7e3" }}>
      <div style={{ ...labelStyle, fontSize: 14 }}>署名欄の位置（テンプレートを使わない送信）</div>
      <div style={{ fontSize: 12, color: "#6b6b68", lineHeight: 1.7, marginBottom: 10 }}>
        書類種別などの値ごとに、署名欄（押印）を置く位置を登録します。送信のとき、<b>1 本目のファイルの最後のページ</b>に置きます。
        単位はポイント（A4 縦は横 595・縦 842）、原点はページの左上です。空欄の相手には置きません。
        <br />
        登録すると、<b>対応表に無い値・空の値のレコードは送信できません</b>（署名欄の無い契約書を出さないため）。
        テンプレートを使う送信には置きません。
      </div>

      <div style={labelStyle}>判定に使うプロパティ</div>
      <PropSelect
        value={prop}
        properties={properties}
        // プロパティを変えたら、前のプロパティの値で作った対応表は使えない
        onChange={(v) => onChange({ stamp_prop: v, stamp_map: v === prop ? map : [] })}
      />

      {prop && (
        <div style={{ marginTop: 14, overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "4px 8px 4px 0", color: "#9a9a96", fontWeight: 500 }}>
                  値{isEnum ? "" : "（レコードの値をそのまま入力）"}
                </th>
                {roles.map((r) => (
                  <th key={String(r.key)} style={{ textAlign: "left", padding: "4px 8px", color: "#9a9a96", fontWeight: 500 }}>
                    {r.label}（x, y）
                  </th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {map.map((entry, i) => (
                <tr key={i}>
                  <td style={{ padding: "4px 8px 4px 0", minWidth: 180 }}>
                    {isEnum ? (
                      <select
                        style={{ ...selectStyle, padding: "6px 8px" }}
                        aria-label="値"
                        value={entry.value}
                        onChange={(e) => {
                          const next = [...map];
                          next[i] = { ...entry, value: e.target.value };
                          setMap(next);
                        }}
                      >
                        <option value="">-- 選択してください --</option>
                        {propOptions.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                        {entry.value && !propOptions.some((o) => o.value === entry.value) && (
                          <option value={entry.value}>（選択肢にありません）{entry.value}</option>
                        )}
                      </select>
                    ) : (
                      <input
                        style={{ ...inputStyle, padding: "6px 8px" }}
                        aria-label="値"
                        value={entry.value}
                        onChange={(e) => {
                          const next = [...map];
                          next[i] = { ...entry, value: e.target.value };
                          setMap(next);
                        }}
                      />
                    )}
                  </td>
                  {roles.map((r) => {
                    const pos = positionOf(entry, r.key);
                    return (
                      <td key={String(r.key)} style={{ padding: "4px 8px", whiteSpace: "nowrap", verticalAlign: "top" }}>
                        {numberInput(i, r.key, "x", `${r.label} の x`)}{" "}
                        {numberInput(i, r.key, "y", `${r.label} の y`)}
                        {isHalf(pos) && (
                          <div style={{ fontSize: 11, color: "#A32D2D", marginTop: 2 }}>x と y の両方を入力</div>
                        )}
                      </td>
                    );
                  })}
                  <td style={{ padding: "4px 0" }}>
                    <button
                      style={{ ...btnSecondary, fontSize: 12, padding: "6px 14px" }}
                      onClick={() => setMap(map.filter((_, j) => j !== i))}
                    >
                      削除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button
            style={{ ...btnSecondary, fontSize: 12, padding: "6px 14px", marginTop: 8 }}
            onClick={() => setMap([...map, { value: "", first_signer: null, recipients: [] }])}
          >
            + 位置を追加
          </button>
        </div>
      )}
    </div>
  );
}
